import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { SharedUiModule } from '@pf/shared-ui';
import { PhotoCaptureFile } from '@cpw/domain';
import { BrowserStorageService } from '@pf/shared-services';

@Component({
    selector: 'cpw-photo-capture',
    imports: [SharedUiModule],
    templateUrl: './cpw-photo-capture.component.html',
    styleUrl: './cpw-photo-capture.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwPhotoCaptureComponent {
    private static readonly CAMERA_ACKNOWLEDGED_KEY = 'cpw_camera_acknowledged';
    private readonly storageService = inject(BrowserStorageService);
    readonly isMobile = CpwPhotoCaptureComponent.detectMobile();

    @ViewChild('cameraInput') cameraInput?: ElementRef<HTMLInputElement>;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    readonly disabled = input<boolean>(false);
    readonly captureLabel = input<string>('Take Photo');
    readonly uploadLabel = input<string>('Upload from Device');

    readonly photoCaptured = output<PhotoCaptureFile>();
    readonly photoDeleted = output<void>();

    readonly showSoftPrompt = signal(false);

    private static detectMobile(): boolean {
        return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(
            navigator.userAgent
        );
    }

    private isCameraAcknowledged(): boolean {
        return (
            this.storageService.getItem<boolean>(
                CpwPhotoCaptureComponent.CAMERA_ACKNOWLEDGED_KEY
            ) === true
        );
    }

    private setCameraAcknowledged(): void {
        this.storageService.setItem(
            CpwPhotoCaptureComponent.CAMERA_ACKNOWLEDGED_KEY,
            true
        );
    }

    onCapturePhoto() {
        if (!this.isMobile) {
            return;
        }

        if (!this.isCameraAcknowledged()) {
            this.showSoftPrompt.set(true);
            return;
        }

        this.triggerCameraInput();
    }

    onSelectPhoto() {
        this.fileInput.nativeElement.click();
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }
        const file = input.files[0];
        const thumbUrl = URL.createObjectURL(file);
        const capturedFile: PhotoCaptureFile = {
            uid: crypto.randomUUID(),
            name: file.name,
            thumbUrl,
            file,
        };
        this.photoCaptured.emit(capturedFile);
        input.value = '';
    }

    onSoftPromptConfirm() {
        this.setCameraAcknowledged();
        this.showSoftPrompt.set(false);
        this.triggerCameraInput();
    }

    onSoftPromptDismiss() {
        this.showSoftPrompt.set(false);
    }

    private triggerCameraInput() {
        this.cameraInput?.nativeElement.click();
    }
}
